import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Briefcase, MagnifyingGlass, X, Plus, Minus, CaretRight, CaretDown, ClipboardText, Lightning, Stack, BookmarkSimple, FloppyDisk, Trash } from "@phosphor-icons/react";

const GEO_ICON = { REGION: "Région", DEPARTMENT: "Département", CITY: "Ville", METRO_AREA: "Métropole", COUNTRY: "Pays", CUSTOM: "Libre" };

function TriCheckbox({ checked, indeterminate, disabled, onChange, testid }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate && !checked; }, [indeterminate, checked]);
  return (
    <input ref={ref} type="checkbox" checked={!!checked} disabled={disabled} onChange={onChange} data-testid={testid}
      className="accent-[#00E5FF] w-3.5 h-3.5 cursor-pointer disabled:cursor-default disabled:opacity-80 shrink-0" />
  );
}

function Chip({ label, mode, onRemove, onToggleMode }) {
  const exc = mode === "EXCLUDE";
  return (
    <span data-testid="targeting-chip" className={`inline-flex items-center gap-1 text-xs rounded-full border px-2.5 py-1 ${exc ? "border-red-400/50 bg-red-500/10 text-red-300" : "border-accent/40 bg-accent/10 text-accent"}`}>
      {onToggleMode && <button onClick={onToggleMode} title={exc ? "Passer en inclusion" : "Passer en exclusion"} className="opacity-70 hover:opacity-100">{exc ? <Minus size={11} /> : <Plus size={11} />}</button>}
      {label}
      <button onClick={onRemove} className="opacity-70 hover:opacity-100"><X size={11} /></button>
    </span>
  );
}

/* ------------------------- GEO MODAL ------------------------- */
function GeoModal({ open, onClose, onSaved }) {
  const [tree, setTree] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [childCache, setChildCache] = useState({});
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [paste, setPaste] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    api.get("/admin/targeting/geo").then(({ data }) => setSelected((data.items || []).map((i) => ({
      geo_id: i.geo_id, raw: i.raw, label: i.label, geo_type: i.geo_type, code: i.geo_id ? i.geo_id.split(":")[1] : null,
      region_code: null, department_code: null, mode: i.mode, include_children: i.include_children }))));
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/geo/tree").then(({ data }) => setTree(data));
    reload();
  }, [open, reload]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/admin/geo/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data.items || [])), 200);
    return () => clearTimeout(t);
  }, [q]);

  const included = selected.filter((s) => s.mode !== "EXCLUDE");
  const includedIds = new Set(included.map((s) => s.geo_id).filter(Boolean));

  const isChecked = (node) => {
    if (includedIds.has(node.id)) return true;
    if (node.geo_type === "DEPARTMENT") return !!(node.region_code && includedIds.has(`R:${node.region_code}`));
    if (node.geo_type === "CITY") return !!((node.department_code && includedIds.has(`D:${node.department_code}`)) || (node.region_code && includedIds.has(`R:${node.region_code}`)));
    return false;
  };
  const isIndeterminate = (node) => {
    if (isChecked(node)) return false;
    if (node.geo_type === "REGION") return included.some((s) => s.region_code === node.code && s.geo_type !== "REGION");
    if (node.geo_type === "DEPARTMENT") return included.some((s) => s.geo_type === "CITY" && s.department_code === node.code);
    return false;
  };
  const coveredByAncestor = (node) => isChecked(node) && !includedIds.has(node.id);

  const isDescendantOf = (s, node) => {
    if (node.geo_type === "REGION") return s.region_code === node.code && s.geo_type !== "REGION";
    if (node.geo_type === "DEPARTMENT") return s.geo_type === "CITY" && s.department_code === node.code;
    return false;
  };

  const toggleNode = (node) => {
    if (coveredByAncestor(node)) return;
    if (includedIds.has(node.id)) {
      setSelected((x) => x.filter((s) => !(s.geo_id === node.id && s.mode !== "EXCLUDE")));
      return;
    }
    setSelected((x) => {
      const cleaned = x.filter((s) => !isDescendantOf(s, node));
      return [...cleaned, { geo_id: node.id, label: node.label, geo_type: node.geo_type, code: node.code,
        region_code: node.region_code, department_code: node.department_code, mode: "INCLUDE", include_children: true }];
    });
  };

  const remove = (i) => setSelected((x) => x.filter((_, idx) => idx !== i));
  const toggleMode = (i) => setSelected((x) => x.map((s, idx) => idx === i ? { ...s, mode: s.mode === "EXCLUDE" ? "INCLUDE" : "EXCLUDE" } : s));

  const selectAllRegions = () => {
    if (!tree) return;
    const regions = (tree.regions || []).map((r) => ({ geo_id: r.id, label: r.label, geo_type: r.geo_type, code: r.code,
      region_code: r.region_code, department_code: null, mode: "INCLUDE", include_children: true }));
    setSelected((x) => {
      const customsAndExcludes = x.filter((s) => s.geo_type === "CUSTOM" || s.mode === "EXCLUDE");
      return [...regions, ...customsAndExcludes];
    });
  };

  const toggleExpand = async (node) => {
    const id = node.id;
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
    if (!childCache[id]) {
      const { data } = await api.get(`/admin/geo/tree?parent=${encodeURIComponent(id)}`);
      setChildCache((c) => ({ ...c, [id]: data }));
    }
  };

  const doPaste = async () => {
    if (!paste.trim()) return;
    const { data } = await api.post("/admin/geo/resolve-paste", { text: paste });
    const add2 = (data.matched || []).filter((m) => !selected.some((s) => s.geo_id === m.id));
    setSelected((x) => [...x, ...add2.map((m) => ({ geo_id: m.id, label: m.label, geo_type: m.geo_type,
      code: m.code, region_code: m.region_code, department_code: m.department_code, mode: "INCLUDE", include_children: true }))]);
    if (data.review?.length) toast.warning(`${data.review.length} non résolu(s) : ${data.review.map((r) => r.raw).join(", ")}`);
    if (add2.length) toast.success(`${add2.length} localisation(s) ajoutée(s)`);
    setPaste("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/targeting/geo", { selections: selected.map((s) => ({
        geo_id: s.geo_id, raw: s.geo_type === "CUSTOM" ? s.label : undefined, mode: s.mode, include_children: s.include_children })) });
      toast.success(`Enregistré — ${data.summary.cities_covered} villes couvertes`);
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const countLabel = (n) => {
    if (n.geo_type === "REGION" && n.counts) return `${n.counts.departments} dép. · ${n.counts.cities} villes`;
    if (n.geo_type === "DEPARTMENT" && n.counts) return `${n.counts.cities} villes`;
    if (n.geo_type === "METRO_AREA" && n.city_count != null) return `${n.city_count} villes`;
    return GEO_ICON[n.geo_type];
  };

  const Node = ({ n, depth }) => {
    const disabled = coveredByAncestor(n);
    return (
      <div className="flex items-center gap-1.5 py-1 hover:bg-surface-2/50 rounded px-1" style={{ paddingLeft: depth * 14 }}>
        {n.has_children ? <button onClick={() => toggleExpand(n)} className="text-muted">{expanded[n.id] ? <CaretDown size={12} /> : <CaretRight size={12} />}</button> : <span className="w-3" />}
        <TriCheckbox checked={isChecked(n)} indeterminate={isIndeterminate(n)} disabled={disabled} onChange={() => toggleNode(n)} testid={`geo-check-${n.id}`} />
        <button onClick={() => toggleNode(n)} disabled={disabled} title={disabled ? "Couvert par une zone parente sélectionnée" : ""} data-testid={`geo-tree-add-${n.id}`} className="flex-1 text-left text-sm text-fg/85 hover:text-accent disabled:opacity-70 disabled:cursor-default">
          {n.label} <span className="text-[10px] text-muted ml-1">{countLabel(n)}</span>
        </button>
      </div>
    );
  };
  const renderTree = () => (tree.regions || []).map((r) => (
    <div key={r.id}>
      <Node n={r} depth={0} />
      {expanded[r.id] && (childCache[r.id]?.departments || []).map((d) => (
        <div key={d.id}>
          <Node n={d} depth={1} />
          {expanded[d.id] && (childCache[d.id]?.cities || []).map((c) => <Node key={c.id} n={c} depth={2} />)}
        </div>
      ))}
    </div>
  ));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-4xl max-h-[92vh] overflow-hidden" data-testid="geo-modal">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><MapPin size={20} className="text-accent" /> Sélectionner les localisations</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3 text-xs border-b border-border pb-2 -mt-1">
          <button onClick={selectAllRegions} className="text-accent hover:underline flex items-center gap-1" data-testid="geo-select-all">Tout cocher ({(tree?.regions || []).length} régions)</button>
          <span className="text-border">·</span>
          <button onClick={() => setSelected([])} className="text-muted hover:text-red-400" data-testid="geo-clear-all">Tout effacer</button>
          <span className="text-border">·</span>
          <button onClick={reload} className="text-muted hover:text-fg" data-testid="geo-reset">Réinitialiser</button>
        </div>
        <div className="grid grid-cols-2 gap-4" style={{ maxHeight: "68vh" }}>
          <div className="overflow-y-auto pr-2">
            <div className="relative mb-2">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-muted" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="geo-search-input" placeholder="Rechercher (Lille, IDF, Nord, métropole…)" className="w-full bg-surface-2 border border-border rounded-md pl-8 pr-3 py-2 text-sm text-fg" />
            </div>
            {results.length > 0 ? (
              <div className="border border-border rounded-md divide-y divide-border mb-2">
                {results.map((r) => <button key={r.id} onClick={() => { toggleNode({ id: r.id, label: r.label, geo_type: r.geo_type, code: r.code, region_code: r.region_code, department_code: r.department_code }); setQ(""); setResults([]); }} data-testid={`geo-result-${r.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-surface-2 flex items-center justify-between">
                  <span>{r.label} <span className="text-[10px] text-muted ml-1">{GEO_ICON[r.geo_type]}{r.region_name ? ` · ${r.region_name}` : ""}</span></span><Plus size={13} className="text-accent" /></button>)}
              </div>
            ) : tree && (
              <div className="text-sm">
                {renderTree()}
                <div className="mt-2 pt-2 border-t border-border overline">Aires métropolitaines</div>
                {(tree.metros || []).map((m) => <Node key={m.id} n={m} depth={0} />)}
              </div>
            )}
            <div className="mt-3">
              <div className="overline mb-1 flex items-center gap-1"><ClipboardText size={12} /> Coller une liste (virgules / points-virgules / lignes)</div>
              <textarea value={paste} onChange={(e) => setPaste(e.target.value)} data-testid="geo-paste" className="w-full bg-surface-2 border border-border rounded-md text-xs px-2 py-1.5 min-h-[54px]" placeholder="Paris; Lille; Lyon; Toulouse" />
              <button onClick={doPaste} className="text-xs text-accent hover:underline mt-1" data-testid="geo-paste-btn">Résoudre & ajouter</button>
            </div>
          </div>
          <div className="overflow-y-auto border-l border-border pl-4">
            <div className="flex items-center justify-between mb-2"><div className="overline">Sélection ({included.length})</div>{selected.length > 0 && <button onClick={() => setSelected([])} className="text-xs text-muted hover:text-red-400" data-testid="geo-clear">Tout effacer</button>}</div>
            <div className="flex flex-wrap gap-1.5">
              {selected.map((s, i) => <Chip key={i} label={s.label} mode={s.mode} onRemove={() => remove(i)} onToggleMode={() => toggleMode(i)} />)}
              {selected.length === 0 && <p className="text-xs text-muted">Aucune localisation. Cochez une région/ville ou recherchez.</p>}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button onClick={onClose} className="text-sm text-muted px-4 py-2">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="geo-save" className="bg-accent text-black text-sm font-semibold px-5 py-2 rounded-md hover-lift disabled:opacity-60">Appliquer</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- ROLE MODAL ------------------------- */
function RoleModal({ open, onClose, onSaved }) {
  const [families, setFamilies] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [paste, setPaste] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    api.get("/admin/targeting/roles").then(({ data }) => setSelected((data.items || []).map((i) => ({
      kind: i.kind, code: i.code, raw: i.raw, label: i.label, mode: i.mode }))));
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/roles/tree").then(({ data }) => setFamilies(data.families || []));
    reload();
  }, [open, reload]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/admin/roles/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data.items || [])), 200);
    return () => clearTimeout(t);
  }, [q]);

  const included = selected.filter((s) => s.mode !== "EXCLUDE");
  const familySelected = (code) => included.some((s) => s.kind === "FAMILY" && s.code === code);
  const roleIncluded = (code) => included.some((s) => s.kind === "ROLE" && s.code === code);

  const familyChecked = (f) => familySelected(f.code) || (f.roles.length > 0 && f.roles.every((r) => roleIncluded(r.code)));
  const familyIndeterminate = (f) => !familyChecked(f) && f.roles.some((r) => roleIncluded(r.code));
  const roleChecked = (f, code) => roleIncluded(code) || familySelected(f.code);
  const roleCovered = (f) => familySelected(f.code);

  const toggleFamily = (f) => {
    if (familySelected(f.code)) {
      setSelected((x) => x.filter((s) => !(s.kind === "FAMILY" && s.code === f.code && s.mode !== "EXCLUDE")));
      return;
    }
    const roleCodes = new Set(f.roles.map((r) => r.code));
    setSelected((x) => {
      const cleaned = x.filter((s) => !(s.kind === "ROLE" && roleCodes.has(s.code) && s.mode !== "EXCLUDE"));
      return [...cleaned, { kind: "FAMILY", code: f.code, label: f.label, mode: "INCLUDE" }];
    });
  };
  const toggleRole = (f, r) => {
    if (roleCovered(f)) return;
    if (roleIncluded(r.code)) {
      setSelected((x) => x.filter((s) => !(s.kind === "ROLE" && s.code === r.code && s.mode !== "EXCLUDE")));
      return;
    }
    setSelected((x) => [...x, { kind: "ROLE", code: r.code, label: r.label, mode: "INCLUDE" }]);
  };
  const addRoleFromSearch = (code, label) => { if (!selected.some((s) => s.code === code)) setSelected((x) => [...x, { kind: "ROLE", code, label, mode: "INCLUDE" }]); };
  const remove = (i) => setSelected((x) => x.filter((_, idx) => idx !== i));
  const toggleMode = (i) => setSelected((x) => x.map((s, idx) => idx === i ? { ...s, mode: s.mode === "EXCLUDE" ? "INCLUDE" : "EXCLUDE" } : s));

  const selectAllFamilies = () => {
    const fams = families.map((f) => ({ kind: "FAMILY", code: f.code, label: f.label, mode: "INCLUDE" }));
    setSelected((x) => {
      const keep = x.filter((s) => s.kind === "CUSTOM" || s.mode === "EXCLUDE");
      return [...fams, ...keep];
    });
  };

  const doPaste = async () => {
    if (!paste.trim()) return;
    const { data } = await api.post("/admin/roles/resolve-paste", { text: paste });
    const add2 = (data.matched || []).filter((m) => !selected.some((s) => s.code === m.code));
    setSelected((x) => [...x, ...add2.map((m) => ({ kind: "ROLE", code: m.code, label: m.label, mode: "INCLUDE" }))]);
    if (data.review?.length) toast.warning(`${data.review.length} non résolu(s) : ${data.review.map((r) => r.raw).join(", ")}`);
    if (add2.length) toast.success(`${add2.length} rôle(s) ajouté(s)`);
    setPaste("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/targeting/roles", { selections: selected.map((s) => ({
        kind: s.kind, code: s.code, raw: s.kind === "CUSTOM" ? s.label : undefined, label: s.label, mode: s.mode })) });
      toast.success(`Enregistré — ${data.summary.roles_covered} rôles couverts`);
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-4xl max-h-[92vh] overflow-hidden" data-testid="role-modal">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Briefcase size={20} className="text-accent" /> Sélectionner les rôles IT</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3 text-xs border-b border-border pb-2 -mt-1">
          <button onClick={selectAllFamilies} className="text-accent hover:underline flex items-center gap-1" data-testid="role-select-all">Tout cocher ({families.length} familles)</button>
          <span className="text-border">·</span>
          <button onClick={() => setSelected([])} className="text-muted hover:text-red-400" data-testid="role-clear-all">Tout effacer</button>
          <span className="text-border">·</span>
          <button onClick={reload} className="text-muted hover:text-fg" data-testid="role-reset">Réinitialiser</button>
        </div>
        <div className="grid grid-cols-2 gap-4" style={{ maxHeight: "68vh" }}>
          <div className="overflow-y-auto pr-2">
            <div className="relative mb-2">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-muted" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="role-search-input" placeholder="Rechercher un rôle (DevOps, Data, Kubernetes…)" className="w-full bg-surface-2 border border-border rounded-md pl-8 pr-3 py-2 text-sm text-fg" />
            </div>
            {results.length > 0 ? (
              <div className="border border-border rounded-md divide-y divide-border">
                {results.map((r) => <button key={r.canonical_role_code || r.code} onClick={() => { addRoleFromSearch(r.canonical_role_code || r.code, r.canonical_role_name || r.name_fr || r.label); setQ(""); setResults([]); }} data-testid={`role-result-${r.canonical_role_code || r.code}`} className="w-full text-left px-3 py-2 text-sm hover:bg-surface-2 flex items-center justify-between">
                  <span>{r.canonical_role_name || r.name_fr || r.label} <span className="text-[10px] text-muted ml-1">{r.canonical_family_name || ""}</span></span><Plus size={13} className="text-accent" /></button>)}
              </div>
            ) : (
              <div className="text-sm">
                {families.map((f) => (
                  <div key={f.code}>
                    <div className="flex items-center gap-1.5 py-1">
                      <button onClick={() => setExpanded((e) => ({ ...e, [f.code]: !e[f.code] }))} className="text-muted">{expanded[f.code] ? <CaretDown size={12} /> : <CaretRight size={12} />}</button>
                      <TriCheckbox checked={familyChecked(f)} indeterminate={familyIndeterminate(f)} onChange={() => toggleFamily(f)} testid={`role-family-check-${f.code}`} />
                      <button onClick={() => toggleFamily(f)} data-testid={`role-family-add-${f.code}`} className="flex-1 text-left font-semibold text-fg/90 hover:text-accent">{f.label} <span className="text-[10px] text-muted">({f.role_count})</span></button>
                    </div>
                    {expanded[f.code] && f.roles.map((r) => {
                      const disabled = roleCovered(f);
                      return (
                        <div key={r.code} className="flex items-center gap-1.5 pl-6 py-0.5">
                          <TriCheckbox checked={roleChecked(f, r.code)} disabled={disabled} onChange={() => toggleRole(f, r)} testid={`role-check-${r.code}`} />
                          <button onClick={() => toggleRole(f, r)} disabled={disabled} data-testid={`role-add-${r.code}`} title={(r.aliases || []).join(", ")} className="text-left text-sm text-fg/75 hover:text-accent disabled:opacity-70 disabled:cursor-default">{r.label}</button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3">
              <div className="overline mb-1 flex items-center gap-1"><ClipboardText size={12} /> Coller une liste de rôles</div>
              <textarea value={paste} onChange={(e) => setPaste(e.target.value)} data-testid="role-paste" className="w-full bg-surface-2 border border-border rounded-md text-xs px-2 py-1.5 min-h-[54px]" placeholder="Data Engineer, DevOps Engineer, Cloud Architect" />
              <button onClick={doPaste} className="text-xs text-accent hover:underline mt-1" data-testid="role-paste-btn">Résoudre & ajouter</button>
            </div>
          </div>
          <div className="overflow-y-auto border-l border-border pl-4">
            <div className="flex items-center justify-between mb-2"><div className="overline">Sélection ({included.length})</div>{selected.length > 0 && <button onClick={() => setSelected([])} className="text-xs text-muted hover:text-red-400" data-testid="role-clear">Tout effacer</button>}</div>
            <div className="flex flex-wrap gap-1.5">
              {selected.map((s, i) => <Chip key={i} label={`${s.label}${s.kind === "FAMILY" ? " (famille)" : ""}`} mode={s.mode} onRemove={() => remove(i)} onToggleMode={() => toggleMode(i)} />)}
              {selected.length === 0 && <p className="text-xs text-muted">Aucun rôle. Cochez une famille ou un rôle.</p>}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <button onClick={onClose} className="text-sm text-muted px-4 py-2">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="role-save" className="bg-accent text-black text-sm font-semibold px-5 py-2 rounded-md hover-lift disabled:opacity-60">Appliquer</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- PRESETS ------------------------- */
function PresetsBar({ onApplied }) {
  const [presets, setPresets] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/admin/targeting/presets").then(({ data }) => setPresets(data.items || [])); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!name.trim()) { toast.error("Nom du preset requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/targeting/presets", { name: name.trim() });
      toast.success(data.updated ? `Preset « ${name.trim()} » mis à jour` : `Preset « ${name.trim()} » enregistré`);
      setName(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  const apply = async (p) => {
    try {
      await api.post(`/admin/targeting/presets/${p.id}/apply`);
      toast.success(`Preset « ${p.name} » appliqué`);
      onApplied && onApplied();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Supprimer le preset « ${p.name} » ?`)) return;
    await api.delete(`/admin/targeting/presets/${p.id}`);
    toast.success("Preset supprimé"); load();
  };

  return (
    <div className="surface rounded-lg p-5 md:col-span-2" data-testid="targeting-presets">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <div className="overline flex items-center gap-2"><BookmarkSimple size={13} className="text-accent" /> Presets de ciblage</div>
          <div className="text-xs text-muted mt-1 max-w-lg">Sauvegardez la combinaison actuelle (localisations + rôles) sous un nom, puis rechargez-la en 1 clic.</div>
        </div>
        <div className="flex items-center gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Nom du preset (ex. Lille IT)" data-testid="preset-name-input"
            className="bg-surface-2 border border-border rounded-md px-3 py-2 text-sm text-fg w-52" />
          <button onClick={save} disabled={saving} data-testid="preset-save-btn" className="flex items-center gap-1.5 bg-accent text-black text-sm font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-60">
            <FloppyDisk size={14} weight="bold" /> Sauvegarder
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="preset-list">
        {presets.length === 0 && <p className="text-xs text-muted">Aucun preset enregistré pour l'instant.</p>}
        {presets.map((p) => (
          <div key={p.id} data-testid={`preset-${p.id}`} className="flex items-center gap-2 border border-border rounded-md pl-3 pr-1.5 py-1.5 bg-surface-2/40">
            <div>
              <div className="text-sm text-fg/90 font-medium">{p.name}</div>
              <div className="text-[10px] text-muted font-mono">{p.cities_covered ?? "?"} villes · {p.roles_covered ?? "?"} rôles</div>
            </div>
            <button onClick={() => apply(p)} data-testid={`preset-apply-${p.id}`} className="text-xs font-semibold text-accent border border-accent/40 rounded px-2 py-1 hover-lift">Appliquer</button>
            <button onClick={() => del(p)} data-testid={`preset-delete-${p.id}`} title="Supprimer" className="text-muted hover:text-red-400 p-1"><Trash size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------- SIMULATION (TargetingCompiler) ------------------------- */
const GRAN_LABEL = { country: "National", region: "Par région", city: "Par ville" };
const SRC_LABEL = { canonical: "Ciblage canonique", fallback: "Repli mots-clés partagés", empty: "Vide", legacy_shared_terms: "Legacy" };

function CompilePreview() {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);

  const simulate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/targeting/compile-preview", {});
      setPlans(data.plans || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de simulation");
    } finally { setLoading(false); }
  };

  return (
    <div className="surface rounded-lg p-5 md:col-span-2" data-testid="targeting-simulate">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline flex items-center gap-2"><Lightning size={13} className="text-accent" /> Simulation de ciblage</div>
          <div className="text-xs text-muted mt-1 max-w-lg">Prévisualise le plan de requêtes optimisé par fournisseur (selon leurs capacités géo & crédits), sans lancer de collecte ni consommer de crédit.</div>
        </div>
        <button onClick={simulate} disabled={loading} data-testid="simulate-targeting-btn" className="bg-accent text-black text-sm font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-60">{loading ? "Calcul…" : "Simuler"}</button>
      </div>
      {plans && (
        <div className="grid sm:grid-cols-3 gap-3" data-testid="simulate-results">
          {plans.map((p) => (
            <div key={p.provider} data-testid={`plan-${p.provider}`} className="border border-border rounded-md p-3 bg-surface-2/40">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm text-fg/90 flex items-center gap-1.5"><Stack size={14} className="text-accent" />{p.label}</div>
                {!p.configured && <span className="text-[10px] text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5">Clé manquante</span>}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
                <div><span className="text-muted">Granularité</span><div className="font-mono text-fg/85">{GRAN_LABEL[p.granularity] || p.granularity}</div></div>
                <div><span className="text-muted">Source</span><div className="text-fg/85">{SRC_LABEL[p.source] || p.source}</div></div>
                <div><span className="text-muted">Requêtes est.</span><div className="font-display text-lg font-bold text-accent" data-testid={`plan-${p.provider}-requests`}>{p.estimated_requests}</div></div>
                <div><span className="text-muted">Crédits est.</span><div className="font-display text-lg font-bold text-fg/90" data-testid={`plan-${p.provider}-credits`}>{p.estimated_credits_max && p.estimated_credits_max !== p.estimated_credits ? `${p.estimated_credits}–${p.estimated_credits_max}` : p.estimated_credits}</div></div>
              </div>
              <div className="mt-1.5 text-[11px] text-muted">{p.role_terms_count || 0} rôle(s) · {p.locations_count || 0} zone(s){p.multi_title ? " · batch multi-titres" : ""}</div>
              {(p.notes || []).map((n, i) => <div key={i} className="mt-1 text-[10px] text-amber-300/80">⚠ {n}</div>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------- MAIN PANEL ------------------------- */
export default function TargetingPanel() {
  const [summary, setSummary] = useState(null);
  const [geoOpen, setGeoOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);

  const load = useCallback(() => { api.get("/admin/targeting/summary").then(({ data }) => setSummary(data)); }, []);
  useEffect(() => { load(); }, [load]);

  const geo = summary?.geo, roles = summary?.roles;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="targeting-panel">
      <PresetsBar onApplied={load} />
      <div className="surface rounded-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <div><div className="overline flex items-center gap-2"><MapPin size={13} className="text-accent" /> Localisations</div>
            <div className="font-display text-2xl font-bold mt-1">{geo ? geo.cities_covered : "…"} <span className="text-sm text-muted font-mono">villes couvertes</span></div>
            <div className="text-xs text-muted">{geo ? `${geo.targets} zone(s)${geo.excludes ? ` · ${geo.excludes} exclusion(s)` : ""}` : ""}</div>
          </div>
          <button onClick={() => setGeoOpen(true)} data-testid="edit-locations-btn" className="bg-accent text-black text-sm font-semibold px-4 py-2 rounded-md hover-lift">Modifier</button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(geo?.chips || []).slice(0, 8).map((c) => <span key={c.id} className="text-xs rounded-full border border-accent/30 bg-accent/10 text-accent px-2 py-0.5">{c.label}</span>)}
          {(geo?.chips || []).length > 8 && <span className="text-xs text-muted">+{geo.chips.length - 8} autres</span>}
        </div>
      </div>
      <div className="surface rounded-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <div><div className="overline flex items-center gap-2"><Briefcase size={13} className="text-accent" /> Rôles IT</div>
            <div className="font-display text-2xl font-bold mt-1">{roles ? roles.roles_covered : "…"} <span className="text-sm text-muted font-mono">rôles couverts</span></div>
            <div className="text-xs text-muted">{roles ? `${roles.targets} cible(s)${roles.families ? ` · ${roles.families} famille(s)` : ""}${roles.excludes ? ` · ${roles.excludes} exclusion(s)` : ""}` : ""}</div>
          </div>
          <button onClick={() => setRoleOpen(true)} data-testid="edit-roles-btn" className="bg-accent text-black text-sm font-semibold px-4 py-2 rounded-md hover-lift">Modifier</button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(roles?.chips || []).slice(0, 8).map((c) => <span key={c.id} className="text-xs rounded-full border border-accent/30 bg-accent/10 text-accent px-2 py-0.5">{c.label}</span>)}
          {(roles?.chips || []).length > 8 && <span className="text-xs text-muted">+{roles.chips.length - 8} autres</span>}
        </div>
      </div>
      <GeoModal open={geoOpen} onClose={() => setGeoOpen(false)} onSaved={load} />
      <RoleModal open={roleOpen} onClose={() => setRoleOpen(false)} onSaved={load} />
      <CompilePreview />
    </div>
  );
}
